import { ShopifyEntityKind } from "@intelligems/ig-types/schemas/ai/nodes/input-requirement";
import { ShopifyItemPickerItem } from "@src/components/MainApp/ShopifyItemPicker/ShopifyItemPicker";
import { capitalizeFirstLetter } from "@intelligems/ig-utils/common";

/** The product picker's store key for this dialog, so no other picker's open state reads as ours. */
export const AI_USER_INPUT_PRODUCT_PICKER_TYPE = "aiUserInput";

/** One picked catalog item: `id` is what the agent reads, `label` what the merchant sees. */
export type ShopifyEntityPick = ShopifyItemPickerItem;

/**
 * Merchant-facing words for each entity kind, shared by the survey card's
 * button copy and the picker dialogs' titles.
 */
export const SHOPIFY_ENTITY_COPY: Record<
  ShopifyEntityKind,
  {
    singular: string;
    plural: string;
    /** The dialog title and the card's "Choose …" button. */
    choose: string;
    searchPlaceholder: string;
  }
> = {
  product: {
    singular: "product",
    plural: "products",
    choose: "Choose products",
    searchPlaceholder: "Search products",
  },
  collection: {
    singular: "collection",
    plural: "collections",
    choose: "Choose collections",
    searchPlaceholder: "Search collections",
  },
  tag: {
    singular: "product tag",
    plural: "product tags",
    choose: "Choose product tags",
    searchPlaceholder: "Search product tags",
  },
  vendor: {
    singular: "vendor",
    plural: "vendors",
    choose: "Choose vendors",
    searchPlaceholder: "Search vendors",
  },
  product_type: {
    singular: "product type",
    plural: "product types",
    choose: "Choose product types",
    searchPlaceholder: "Search product types",
  },
};

/**
 * Shopify's own reserved product type for gift cards, stored as one word. It
 * is not a merchant's type name, so it reads as the two words it stands for.
 */
const SHOPIFY_PRODUCT_TYPE_LABELS: Record<string, string> = {
  giftcard: "Gift card",
  gift_card: "Gift card",
};

/**
 * A catalog value as an option row shows it (ENG-7739). Product types come
 * straight from the catalog as written — often a lowercase slug like
 * `accessories` — so a type written entirely in lowercase is sentence-cased
 * (`Accessories`, `Gift card`), with `_`/`-` read as spaces. A type the
 * merchant wrote with capitals is theirs and is left alone, and so is every
 * other kind: a tag or vendor is matched by its exact string.
 *
 * Display only: the pick still sends the raw value as its id.
 */
export const formatCatalogLabel = (
  entity: ShopifyEntityKind,
  label: string
): string => {
  if (entity !== "product_type") {
    return label;
  }
  const trimmed = label.trim();
  const known = SHOPIFY_PRODUCT_TYPE_LABELS[trimmed.toLowerCase()];
  if (known) {
    return known;
  }
  if (trimmed !== trimmed.toLowerCase()) {
    return label;
  }
  const words = trimmed.replace(/[_-]+/g, " ").replace(/\s+/g, " ");
  return capitalizeFirstLetter(words);
};

/** The button and title copy for a pick, singular when only one item is the answer. */
export const shopifyEntityChooseLabel = (
  entity: ShopifyEntityKind,
  multiSelect: boolean
): string => {
  const copy = SHOPIFY_ENTITY_COPY[entity];
  return multiSelect ? copy.choose : `Choose a ${copy.singular}`;
};
